import { parseCookies, setCookie, destroyCookie } from 'nookies';

import { cookies } from '../constants/cookies';

type CookieSerializeOptions = {
  path?: string;
  expires?: Date;
  maxAge?: number;
  domain?: string;
};

const SHARED_AUTH_COOKIE_DOMAIN = '.cryptocontrol.pro';

function shouldUseSharedAuthCookieDomain(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { hostname } = window.location;

  return (
    hostname === 'cryptocontrol.pro' ||
    hostname.endsWith(SHARED_AUTH_COOKIE_DOMAIN)
  );
}

function getCookieOptions(
  name: string,
  options: CookieSerializeOptions
): CookieSerializeOptions {
  if (name !== cookies.AUTH_TOKEN || !shouldUseSharedAuthCookieDomain()) {
    return options;
  }

  return {
    ...options,
    domain: SHARED_AUTH_COOKIE_DOMAIN,
  };
}

function getCookieOptionVariants(
  name: string,
  options: CookieSerializeOptions
): CookieSerializeOptions[] {
  if (name !== cookies.AUTH_TOKEN || !shouldUseSharedAuthCookieDomain()) {
    return [options];
  }

  return [options, getCookieOptions(name, options)];
}

interface IUseCookiesData {
  readonly getItem: (name: string) => string;
  readonly destroyAll: () => void;
  readonly removeItem: (name: string, options?: CookieSerializeOptions) => void;
  readonly setItem: (
    name: string,
    data: string,
    options?: CookieSerializeOptions
  ) => void;
}

export const useCookies = (): IUseCookiesData => {
  function getItem(name: string): string {
    const { [name]: item } = parseCookies();
    return item;
  }

  function setItem(
    name: string,
    data: string,
    options: CookieSerializeOptions = {
      path: '/',
      expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365 * 5),
      maxAge: 60 * 60 * 24 * 365 * 5,
    }
  ): void {
    if (name === cookies.AUTH_TOKEN) {
      destroyCookie(null, name, options);
    }

    setCookie(null, name, data, getCookieOptions(name, options));
  }

  function removeItem(
    name: string,
    options: CookieSerializeOptions = {
      path: '/',
    }
  ): void {
    getCookieOptionVariants(name, options).forEach((cookieOptions) => {
      destroyCookie(null, name, cookieOptions);
    });
  }

  function destroyAll(): void {
    const cookies = parseCookies();
    Object.keys(cookies).forEach((cookieName) => {
      if (cookieName.startsWith('@CryptoControlV2')) {
        getCookieOptionVariants(cookieName, { path: '/' }).forEach(
          (cookieOptions) => {
            destroyCookie(null, cookieName, cookieOptions);
          }
        );
      }
    });
  }

  return {
    getItem,
    setItem,
    removeItem,
    destroyAll,
  };
};
